/* eslint-disable no-nested-ternary */
import React from "react";
import { Flex } from "../../../components/Flex";
import styled from "styled-components";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { Text } from "../../../components/Text";
import { Shadow } from "../../../theme/Shadow";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { ThemeType } from "../../../theme/Theme";
import { ImageCreateEdit } from "../../../components/ImageCreateEdit";
import { UserProfileWrapper } from "../../../components/UserProfileWrapper";

import { Button } from "../../../components/Button";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";

const Container = styled(Flex)<{ emphasize?: boolean }>`
  border-radius: ${Rounded.md};
  background-color: ${({ theme }) => theme.white};
  box-shadow: ${Shadow.SM};
  padding: ${Spacing.s} ${Spacing.m};
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  z-index: 1;
  border-inline-start: 3px solid
    ${({ theme, emphasize }) => (emphasize ? theme.main : "transparent")};

  /* A faint wash behind the content (same technique as the Info component)
     rather than a solid tint, so the icon and text stay fully legible. */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-color: ${({ theme }) => theme.main};
    opacity: ${({ emphasize }) => (emphasize ? 0.08 : 0)};
    border-radius: inherit;
    z-index: -1;
  }
`;

type ReportWidgetProps = {
  title: string;
  /** What window the number covers, when that is not obvious from the title. */
  subtitle?: string;
  children: React.ReactNode;
  icon?: IconProp;
  image?: string;
  iconColor: keyof ThemeType;
  /** Rendered beside the figure — typically a ReportDelta. */
  badge?: React.ReactNode;
  onClick?: VoidFunction;
  /** This is the aggregate the other tiles in the row are slices of, so it
   * reads first rather than fighting its own subsets for attention. */
  emphasize?: boolean;
};

export const ReportWidget: React.FC<ReportWidgetProps> = ({
  title,
  subtitle,
  children,
  icon,
  iconColor,
  image,
  badge,
  onClick,
  emphasize,
}) => {
  return (
    <Container emphasize={emphasize}>
      <Flex alignItems="center" gap="s">
        {image ? (
          <UserProfileWrapper>
            <ImageCreateEdit src={image} sizes="" />
          </UserProfileWrapper>
        ) : icon ? (
          <AtiraIcon icon={icon} color={iconColor} />
        ) : null}
        <Text fontWeight="bold">{title}</Text>
      </Flex>
      {subtitle ? (
        <Text fontSize="s" color="darkerSub">
          {subtitle}
        </Text>
      ) : null}
      <Flex alignItems="center" gap="s" flexWrap="wrap">
        {children}
        {badge}
      </Flex>
      {onClick ? (
        <Flex alignSelf="flex-end">
          <Button width="3rem" padding="s" icon={faEye} onClick={onClick} />
        </Flex>
      ) : null}
    </Container>
  );
};
